import { LitElement, html } from 'vendor/lit'
import { GWElement } from 'lib/elements'
import broadcastReceiver from 'lib/broadcast-receiver'
import { addToCart } from 'utils/requests'
import { toggleCart, reRenderShopifySections } from 'utils/shopify'
import { cssPropertyValue } from 'utils/dom'
import { serializeForm } from 'utils/general'
import { getSection, getSectionId, imagesLoaded } from 'utils/gw'
class HHProductForm extends LitElement {
  static properties = {
    showCartDrawerAfterSubmission: {
      type: Boolean,
      attribute: 'show-cart-drawer',
    },
  }

  constructor() {
    super()

    this.handleBreakpoint = this.onBreakpoint.bind(this)
    this.handleScroll = this.onScroll.bind(this)
    this.mm = matchMedia('(max-width: 749.9px)')
    this.preorderBackInStock = this.getAttribute('data-preorder-bis') === 'true'
  }

  get form() {
    return this.querySelector('form')
  }

  get submitButton() {
    return this.querySelector('[type="submit"]')
  }

  get bisButton() {
    return this.querySelector('#BIS_trigger')
  }

  get quantityInput() {
    return this.querySelector('[name="quantity"]')
  }

  connectedCallback() {
    super.connectedCallback()

    this.receiver = broadcastReceiver('quantity', (...args) => {
      this.getQuantityFromDocument(...args)
    })

    if (!this.isClone) {
      this.mm.addEventListener('change', this.handleBreakpoint)

      if (this.mm.matches) {
        this.enableClone()
      }
    }

    this.form.addEventListener('submit', this.onSubmitHandler.bind(this))
  }

  disconnectedCallback() {
    super.disconnectedCallback()

    this.receiver.disconnect()

    if (!this.isClone) {
      this.mm.removeEventListener('change', this.handleBreakpoint)

      this.clone?.remove()
    }

    this.form.removeEventListener('submit', this.onSubmitHandler.bind(this))
  }

  onBreakpoint({ matches }) {
    this.toggleClone(matches)
  }

  get isClone() {
    return this.classList.contains('cloned-cta')
  }

  enableClone() {
    if (this.isClone) return

    if (!this.clone) {
      this.clone = this.cloneNode(true)
      this.header = document.querySelector('hh-sticky-header hh-header')

      this.clone.form.id = `${this.clone.form.getAttribute('id')}-clone`
      this.updateCloneStatusId()
      this.clone.classList.add('hidden')
      this.clone.classList.add('cloned-cta')
      document.body.appendChild(this.clone)
    }

    document.addEventListener('scroll', this.handleScroll)
  }

  disableClone() {
    if (this.isClone) return

    document.removeEventListener('scroll', this.handleScroll)
    this.clone?.classList.add('hidden')
  }

  toggleClone(enable = true) {
    if (this.isClone) return

    if (enable) {
      this.enableClone()
    } else {
      this.disableClone()
    }
  }

  updateCloneState() {
    if (this.isClone || !this.clone) return
    this.clone.form.innerHTML = this.form.innerHTML
    this.updateCloneStatusId()
  }

  updateCloneStatusId() {
    const cloneStatus = this.clone?.querySelector('[role="status"][id^="AddToCartStatus-"]')
    if (!cloneStatus) return

    if (!cloneStatus.id.endsWith('-clone')) {
      cloneStatus.id = `${cloneStatus.id}-clone`
    }
    this.clone.submitButton.setAttribute('aria-describedby', cloneStatus.id)
  }

  setButtonState(enabled, text, isPreorder = false) {
    if (text) {
      this.submitButton.textContent = text
    }

    this.submitButton.toggleAttribute('disabled', !enabled)
    this.submitButton.setAttribute('aria-disabled', String(!enabled))

    this.announceButtonState(enabled)

    if (this.bisButton) {
      this.bisButton.classList.toggle('button--secondary', enabled)
      this.bisButton.style.display = enabled && (!isPreorder || !this.preorderBackInStock) ? 'none' : 'block'
    }
  }

  announceButtonState(enabled) {
    const status = this.querySelector('[role="status"][id^="AddToCartStatus-"]')
    const selectedVariant = this.closest('product-wrapper')?.querySelector('hh-variant-picker input:checked')
    const selectedOption = selectedVariant?.value?.split('|')[0]?.trim()
    if (!status || !selectedOption) return

    const message = enabled
      ? window.variantStrings.selectionAvailableStatus
      : window.variantStrings.selectionUnavailableStatus
    const announcement = message.replace('[option]', selectedOption)
    if (status.textContent.trim() === announcement) return

    status.textContent = ''
    requestAnimationFrame(() => {
      status.textContent = announcement
    })
  }

  onScroll() {
    const [headerHeight, freeShippingBarHeight = 0, ctaOffset] = cssPropertyValue(
      this.clone,
      ['--hh-header-height', '--hh-ge-container-height', '--offset-top'],
      { warn: false }
    )

    if (headerHeight === null || ctaOffset === null) return

    const ctaButtonTop = this.getBoundingClientRect().y
    this.clone.classList.toggle('hidden', ctaButtonTop >= headerHeight + freeShippingBarHeight + ctaOffset)
  }

  getQuantityFromDocument(data) {
    if (this.quantityInput) {
      this.quantityInput.value = data.value || 1
    }
  }

  async onSubmitHandler(e) {
    e.preventDefault()

    const invoker = this.submitButton          // ← ADD (line 187-ish, before the disable)

    this.submitButton.setAttribute('disabled', true)
    this.submitButton.classList.add('loading')

    const response = await addToCart(JSON.parse(serializeForm(this.form)))
    if (response !== false && !response.error) {
      reRenderShopifySections(response)

      if (this.showCartDrawerAfterSubmission) {
        toggleCart(true, invoker)                // ← CHANGE line 196
      }

      this.dispatchEvent(
        new CustomEvent('productAdded', {
          bubbles: true,
          composed: true,
          detail: {
            id: 'cart-notification',
            productId: this.form.querySelector('[name="product-id"]').value,
            variantId: this.form.querySelector('[name="id"]').value,
          },
        })
      )

      // Dispatch saved cart item event for customer accounts
      const gwCustomerAccounts = document.querySelector('gw-customer-accounts')
      if (gwCustomerAccounts) {
        const event = new CustomEvent('gw:saved-items-from-cart', {
          detail: {
            productId: this.form.querySelector('[name="product-id"]').value,
            variantId: this.form.querySelector('[name="id"]').value,
            target: e.target,
            extra: {
              ...response,
              handle: this.getAttribute('data-handle'),
            }
          },
        })

        gwCustomerAccounts.dispatchEvent(event)
      }
    } else if (response?.error) {
      console.error('Failed to add to cart', response.error)
    }

    this.submitButton.removeAttribute('disabled')
    this.submitButton.classList.remove('loading')
  }

  render() {
    return html`<slot></slot>`
  }
}

if (!customElements.get('hh-product-form')) {
  customElements.define('hh-product-form', HHProductForm)
}

class ProductWrapper extends GWElement {
  constructor() {
    super()

    this.addListener(this, 'click', this.onLinkClick, false, { capture: true })
    this.addListener(this.eventParent, 'variant:change', this.prefetchOnIdle)
    this.addListener(this.eventParent, 'pdp-transition:complete', this.onTransitionEnd)

    this.closest('stl-dialog')?.addEventListener('shop-the-look:modal-update', this.reset.bind(this))
  }

  init() {
    super.init()

    this.section = getSection(this)
    this.sectionId = this.getAttribute('section-id') ?? getSectionId(this)
    this.productUrl = this.getAttribute('product-url') ?? ''
    this.siblingUrls = JSON.parse(this.getAttribute('sibling-urls')) ?? []
    this.siblingVariantUrlOptionMap =
      JSON.parse(this.querySelector('[data-sibling-variant-url-option-map]').textContent) ?? {}

    this.setBaseUrl()

    this.prefetchOnIdle()
  }

  connectedCallback() {
    super.connectedCallback()
  }

  get variantPicker() {
    return this.querySelector('hh-variant-picker')
  }

  set transitioning(value) {
    window.pdpTransitioning = value
  }

  onTransitionEnd(e) {
    const soldOutProductData = this.variantPicker.getSoldOutProductData()
    const variantData = this.variantPicker.getVariantData()
    window.BIS.popup.product = soldOutProductData
    window.BIS.popup._variantsById = Object.fromEntries(variantData.map(v => [v.id, v]))
    window.BIS.popup._variantsByTitle = Object.fromEntries(variantData.map(v => [v.title, v]))
    window.BIS.popup.variants = soldOutProductData.variants

    window.BIS.popup.createUI()

    if (document.documentElement.classList.contains('has-open-dialog')) {
      document.body.style.position = 'fixed';
    }

    if (!this.closest('.stl-modal')) {
      setTimeout(() => document.querySelector('shop-the-look')?.updateProductData(), 100)
    }

    this.transitioning = false
  }

  setBaseUrl() {
    if (!this.siblingUrls) {
      console.error('ProductWrapper: Missing required attributes "sibling-urls"')
      return
    }
  }

  get currentUrl() {
    let pathname = location.pathname
    if (pathname.endsWith('/')) {
      pathname = pathname.slice(0, -1)
    }

    return pathname
  }

  get currentProductVariant() {
    return this.siblingVariantUrlOptionMap[this.productUrl][this.variantOptionKey]
  }

  onVariantChange(e) {
    const {
      detail: {
        variant: { option1: option },
      },
    } = e
    const optionKey = option ? option.split('|')[0].trim() : null

    this.prefetchOnIdle(optionKey)
  }

  get selectedOption() {
    return this.variantPicker.options?.[0].split('|')[0].trim()
  }

  get variantOptionKey() {
    return this.selectedOption ?? 'default'
  }

  getVariant(productUrl) {
    return this.siblingVariantUrlOptionMap[productUrl][this.variantOptionKey]
  }

  getVariantMarkup(productUrl) {
    const cachedContent = this.siblingVariantUrlOptionMap[productUrl][this.variantOptionKey]?.cache
    return cachedContent !== '' ? cachedContent : null
  }

  // TODO: Consider reworking to fetch each variant on idle rather than all at once
  prefetchOnIdle(optionKey = null) {
    this.prefetch({ optionKey, onIdle: true })
  }

  async prefetch({ invalidateCache = false, optionKey = null, onIdle = false } = {}) {
    const variantMapKey = optionKey ?? this.variantOptionKey

    for (const [url, variantMap] of Object.entries(this.siblingVariantUrlOptionMap)) {
      const variant = variantMap[variantMapKey]

      if (!variant) continue

      if (url === this.currentUrl) {
        if (!variant.cache || invalidateCache) {
          variant.cache = this.section.outerHTML
        }

        continue
      }

      if (variant.cache && !invalidateCache) {
        continue
      }

      const queryParams = new URLSearchParams(window.location.search)
      queryParams.delete('variant')

      const urlQueryJoiner = variant.url.includes('?') ? '&' : '?'
      const variantUrl = `${variant.url}${urlQueryJoiner}${queryParams.toString()}`

      const fetchVariant = async () => {
        try {
          const markup = await this.fetchSectionContent(variantUrl)

          variant.cache = markup
        } catch (error) {
          console.error('ERROR', error)
        }
      }

      if (onIdle) {
        requestIdleCallback(fetchVariant)
      } else {
        fetchVariant()
      }
    }
  }

  onLinkClick(e) {
    const link = e.target.closest('a')
    if (!link) return

    const href = link.getAttribute('href')

    if (!this.siblingUrls.some(url => href.startsWith(url))) return

    e.preventDefault()

    const views = [...this.querySelectorAll('[data-view]')]
    this.views = Object.fromEntries(views.map(view => [view.dataset.view, view]))

    this.replaceSectionContent(href)
  }

  async fetchSectionContent(url) {
    try {
      const urlQueryJoiner = url.includes('?') ? '&' : '?'
      const response = await fetch(url)
      // const response = await fetch(`${url}${urlQueryJoiner}section_id=${this.sectionId}`)
      const markup = await response.text()

      return markup
    } catch (error) {
      console.error('ERROR', error)
      return false
    }
  }

  togglePreventClick(preventClick = true) {
    this.classList.toggle('is-transitioning', preventClick)
    // this.style.cursor = preventClick ? 'wait' : ''
    // this.style.pointerEvents = preventClick ? 'none' : ''
  }

  get eventParent() {
    return this.closest('.stl-modal') ?? document
  }

  async replaceSectionContent(url) {
    const currentVariant = this.currentProductVariant

    try {
      const productUrl = url.split('?')[0]
      const newVariant = this.getVariant(productUrl)
      const markup = this.getVariantMarkup(productUrl) ?? (await this.fetchSectionContent(url))

      const parser = new DOMParser()
      const doc = parser.parseFromString(markup, 'text/html')
      let productWrapper = doc.querySelector('product-wrapper')

      if (this.closest('.stl-modal')) {
        const updatedProductWrapper = await document.querySelector('shop-the-look')?.updateGalleryStatus(productWrapper)
        if (updatedProductWrapper) {
          productWrapper = updatedProductWrapper
        }
      }

      await imagesLoaded(productWrapper, { container: true })
      const views = productWrapper.querySelectorAll('[data-view]')

      this.setAttribute('product-url', productUrl)

      const supportsViewTransition = Boolean(document.startViewTransition)

      const replaceContent = (targetView, view) => {
        return new Promise(resolve => {
          if (view.hasAttribute('data-view-container')) {

            const targetViewComponents = [...targetView.querySelectorAll('[data-view-component]')]
            const viewComponents = [...view.querySelectorAll('[data-view-component]')]

            for (const viewComponent of viewComponents.toReversed()) {
              const currentComponent = targetViewComponents.find(
                b => b.dataset.viewComponent === viewComponent.dataset.viewComponent
              )

              if (!currentComponent) {
                console.warn('NO VIEW COMPONENT TO REPLACE', viewComponent)
                continue
              }

              if (
                supportsViewTransition &&
                customElements.get(currentComponent.localName) &&
                'renderRoot' in currentComponent &&
                currentComponent.renderRoot.innerHTML.replace('\x3C!---->', '') !== '<slot></slot>'
              ) {
                currentComponent.pauseUpdates()
                setTimeout(() => {
                  viewComponent.setAttribute('data-view-component-new', '')
                  const wrapper = currentComponent.parentElement
                  wrapper.appendChild(viewComponent)

                  this.eventParent.addEventListener(
                    'pdp-transition:complete',
                    () => {
                      currentComponent.remove()
                      viewComponent.removeAttribute('data-view-component-new')
                    },
                    { once: true }
                  )
                }, 100)
              } else {
                currentComponent?.replaceWith(viewComponent)
              }
            }
          } else if (targetView) {
            targetView.replaceWith(view)
          }

          setTimeout(() => resolve(), 100)
        })
      }

      if (document.startViewTransition) {
        this.transitioning = true
        if (document.documentElement.classList.contains('has-open-dialog') && !document.documentElement.classList.contains('has-open-stl-dialog')) {
          document.body.style.position = 'relative';
        }
        this.togglePreventClick(true)
      }

      const transitions = []
      for (const view of views) {
        const targetView = this.views[view.dataset.view]

        if (!targetView) continue

        if (!supportsViewTransition) {
          transitions.push(replaceContent(targetView, view))
        } else {
          const callback = replaceContent.bind(this, targetView, view)
          const transition = document.startViewTransition(callback)
          transitions.push(transition)
        }
      }

      await Promise.all(transitions.map(t => t.finished ?? t))

      if (!this.closest('.stl-modal')) {
        this.variantPicker.updateURL()
      }

      this.eventParent.dispatchEvent(
        new CustomEvent('pdp-transition:complete', {
          detail: {
            from: {
              id: currentVariant.id,
              path: currentVariant.url,
            },
            to: {
              id: newVariant.id,
              path: newVariant.url,
            },
          },
        })
      )
    } catch (error) {
      console.error('ERROR', error)
    } finally {
      this.togglePreventClick(false)
    }
  }
}

if (!customElements.get('product-wrapper')) {
  customElements.define('product-wrapper', ProductWrapper)
}
